<template>
  <ValidationField
    v-slot="{ field, errors }"
    :model-value="innerValue"
    :name="name"
    as="div"
    :rules="rules"
    :vid="vid"
    :class="{
      'w-full': fullWidth,
    }"
  >
    <div
      :class="{
        'autocomplete-field': true,
        'autocomplete-field--error': errors.length > 0,
        'autocomplete-field--is-focused': isFocused,
        'w-full': fullWidth,
        ...className
      }"
    >
      <span
        v-if="label"
        class="autocomplete-field__label"
      >
        {{ label }}
      </span>
      <Multiselect
        v-bind="field"
        v-model="innerValue"
        :loading="loading"
        :options="innerOptions"
        :options-limit="optionsLimit"
        :close-on-select="closeOnSelect"
        :clear-on-select="false"
        :preserve-search="true"
        :label="returnObject ? itemLabel : undefined"
        :track-by="returnObject ? itemValue : undefined"
        :multiple="multiple"
        :placeholder="placeholder"
        select-label="選択"
        selected-label=""
        deselect-label="選択解除"
        no-options=""
        open-direction="bottom"
        :custom-label="customLabel"
        :disabled="disabled"
        :searchable="searchable"
        :allow-empty="allowEmpty"
        @open="onOpen"
        @close="onClose"
        @search-change="onSearchChange"
      >
        <template #noResult>
          {{ noResultText }}
        </template>
        <template #noOptions>
          {{ noOptionText }}
        </template>
        <template
          v-if="multiple && modelValue.length > maxResultShown"
          #selection="{ values, isOpen }"
        >
          <span
            v-if="values.length &amp;&amp; !isOpen"
          >
            {{ placeholder }}
          </span>
        </template>
      </Multiselect>
      <div
        v-if="errors.length > 0"
        style="height: 26px;"
        class="con-text-validation span-text-validation-danger vs-input--text-validation-span v-enter-to"
      >
        <span class="span-text-validation">
          {{ errors[0] }}
        </span>
      </div>
    </div>
  </ValidationField>
</template>

<script>
import Multiselect from 'vue-multiselect'

export default {
  name: 'AutocompleteField',
  components: {
    Multiselect
  },
  props: {
    modelValue: {
      type: [String, Object, Array, Number],
      default: null
    },
    itemLabel: {
      type: String,
      default: 'text'
    },
    itemValue: {
      type: String,
      default: 'value'
    },
    label: {
      type: String,
      default: null
    },
    placeholder: {
      type: String,
      default: null
    },
    multiple: {
      type: Boolean,
      default: true
    },
    loading: {
      type: Boolean,
      default: false
    },
    options: {
      type: Array,
      default () {
        return []
      }
    },
    noOptionText: {
      type: String,
      default: '候補がありません'
    },
    noResultText: {
      type: String,
      default: '結果が見つかりません'
    },
    closeOnSelect: {
      type: Boolean,
      default: false
    },
    name: {
      type: String,
      default: null
    },
    fullWidth: {
      type: Boolean,
      default: false
    },
    rules: {
      type: String,
      default: null
    },
    vid: {
      type: String,
      default: null
    },
    returnObject: {
      type: Boolean,
      default: false
    },
    maxResultShown: {
      type: Number,
      default: 0
    },
    disabled: {
      type: Boolean,
      default: false
    },
    searchable: {
      type: Boolean,
      default: true
    },
    allowEmpty: {
      type: Boolean,
      default: true
    },
    optionsLimit: {
      type: Number,
      default: 200  // 検索機能のパフォーマンスに関わる
    }
  },
  emits: ['update:modelValue', 'search-change'],
  data () {
    return {
      isFocused: false,
      isClosed: true
    }
  },
  computed: {
    innerOptions () {
      if (this.returnObject) {
        return this.options
      }
      return this.options.map(option => option[this.itemValue])
    },
    innerValue: {
      get () {
        return this.modelValue
      },
      set (newValue) {
        this.$emit('update:modelValue', newValue)
      }
    },
    className () {
      return {
        'autocomplete-field--is-closed': this.isClosed
      }
    }
  },
  methods: {
    onOpen () {
      this.isFocused = true
      this.isClosed = false
    },
    onClose () {
      this.isFocused = false
      this.isClosed = true
    },
    onSearchChange (value) {
      this.$emit('search-change', value)
    },
    customLabel (option) {
      if (this.returnObject) {
        return option[this.itemLabel]
      }
      const o = this.options.find(o => o[this.itemValue] === option)
      return o ? o[this.itemLabel] : null
    }
  }
}
</script>

<style>
.autocomplete-field {
  width: 200px;
}
.autocomplete-field--is-focused .autocomplete-field__label {
  color: rgba(var(--vs-primary),1)!important;
}
.autocomplete-field .autocomplete-field__label {
  letter-spacing: 0.01rem;
  direction: ltr;
  --vs-light: 245,245,245;
  --vs-primary: 115,103,240;
  --vs-success: 40,199,111;
  --vs-danger: 234,84,85;
  --vs-warning: 255,159,67;
  --vs-dark: 30,30,30;
  --vh: 8.14px;
  font-family: "Montserrat", Helvetica, Arial, sans-serif;
  font-weight: 400;
  line-height: 2 !important;
  text-align: left !important;
  margin: 0;
  box-sizing: border-box;
  outline: none;
  text-transform: none;
  text-decoration: none;
  font-size: .85rem;
  color: rgba(0,0,0,.7);
  padding: 0 0 0 5px;
}
.autocomplete-field .multiselect__tags {
  direction: ltr;
  --vs-light: 245,245,245;
  --vs-primary: 115,103,240;
  --vs-success: 40,199,111;
  --vs-danger: 234,84,85;
  --vs-warning: 255,159,67;
  --vs-dark: 30,30,30;
  --vh: 7.44px;
  -webkit-box-direction: normal;
  margin: 0;
  outline: none;
  text-transform: none;
  text-decoration: none;
  color: inherit;
  position: relative;
  border-radius: 5px;
  box-sizing: border-box;
  box-shadow: 0 0 0 0 rgba(0,0,0,.15);
  transition: all .3s ease;
  width: 100%;
  font-size: 1rem !important;
  border: 1px solid rgba(0, 0, 0, 0.2)!important;
}
.autocomplete-field.autocomplete-field--error .multiselect__tags {
  border: 1px solid rgba(var(--vs-danger),1)!important;
}
.autocomplete-field.autocomplete-field--is-focused .multiselect__tags input {
  top: 3px;
}
.autocomplete-field .multiselect__tags {
  padding-right: 8px !important;
}
.autocomplete-field.autocomplete-field--is-closed .multiselect__tags .multiselect__tag {
  margin-bottom: 0;
}
</style>
